// usePreloadSettings.js
import { useEffect, useRef } from 'react';

// Context
import { useStartupStatus } from 'context/StartupStatusContext';
import { useUser } from 'context/UserContext';

// Services
import { getSettings, subscribeToSettings } from 'services/settings/SettingReadService';

/**
 * Bootstraps user settings once, then keeps them live without re-entering startup loading.
 */
const usePreloadSettings = () => {
  const { isEmailVerified, setIsUserSettingsLoading, user, setUserSettings } = useUser();
  const { clearStatus, setStatus } = useStartupStatus();
  const hasResolvedInitialSettingsRef = useRef(false);

  useEffect(() => {
    if (!user?.key || !isEmailVerified) {
      hasResolvedInitialSettingsRef.current = false;
      clearStatus('startup.settings');
      setIsUserSettingsLoading(false);
      setUserSettings(null);
      return undefined;
    }

    if (hasResolvedInitialSettingsRef.current) {
      return undefined;
    }

    let isDisposed = false;

    setIsUserSettingsLoading(true);
    setStatus('startup.settings', {
      message: 'Loading your settings…',
      priority: 30,
    });

    getSettings(user.key)
      .then((settings) => {
        if (isDisposed) return;

        hasResolvedInitialSettingsRef.current = true;
        clearStatus('startup.settings');
        setIsUserSettingsLoading(false);
        setUserSettings(settings);
      })
      .catch((error) => {
        if (isDisposed || hasResolvedInitialSettingsRef.current) return;

        console.error('[usePreloadSettings] one-shot settings read failed:', error);
        hasResolvedInitialSettingsRef.current = true;
        clearStatus('startup.settings');
        setIsUserSettingsLoading(false);
        setUserSettings(null);
      });

    return () => {
      isDisposed = true;
    };
  }, [
    clearStatus,
    isEmailVerified,
    setIsUserSettingsLoading,
    setStatus,
    setUserSettings,
    user?.key,
  ]);

  useEffect(() => {
    if (!user?.key || !isEmailVerified) {
      return undefined;
    }

    const unsubscribe = subscribeToSettings(user.key, (settings) => {
      setUserSettings(settings);
    }, {
      onError: (error) => {
        console.error('[usePreloadSettings] realtime settings listener error:', error);
      },
    });

    return () => {
      clearStatus('startup.settings');
      if (unsubscribe) unsubscribe();
    };
  }, [clearStatus, isEmailVerified, setUserSettings, user?.key]);
};

export default usePreloadSettings;
